/* eslint-disable prefer-destructuring */
import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    InternalTransferAddTransactionDetailModelDto,
    InternalTransferTransactionModelDto,
    InternalTransfersClient,
    LoanPaymentOptionTypeEnumDto,
    InternalTransferTransactionFilterModelDto as OneTimeFilter,
    RecurringInternalTransferFilterModelDto as RecurringFilter,
} from '@treasury/api/channel';
import { TmHttpClient } from '@treasury/core/http';
import { AddTransactionRequest, Transaction, Transfer, TransferTemplateDetails } from '.';
import { TransferAccount } from './transfer-account.entity';

import {
    getFilteredTransferAccounts,
    getTransferAccounts,
    getTransferFilterFromAccounts,
    getTransferFilterToAccounts,
    getTransferFromAccounts,
    getTransferTemplateAccounts,
    getTransferToAccounts,
} from './transfer-payment-requests';
import { TransferPayment } from './transfer-payment.entity';

export type TransferFilter = OneTimeFilter | RecurringFilter;

@Injectable()
export class TransferService {
    constructor(
        private internalTransfersClient: InternalTransfersClient,
        private http: TmHttpClient
    ) {}

    public async createTransfer(transfer: Transfer | Transfer[]) {
        const dto = Array.isArray(transfer) ? transfer.map(t => t.toDto()) : transfer.toDto();
        let templateName, templateId, transactions: Transaction[], response;
        if (Array.isArray(dto)) {
            transactions = dto.map(t => t.transactions) as unknown as Transaction[];
            response = (
                await this.internalTransfersClient.internalTransfersPost(
                    { type: 'add' },
                    {
                        type: 'many',
                        transactions: transactions.map((t: Transaction[] | Transaction) =>
                            Array.isArray(t)
                                ? (t[0] as unknown as InternalTransferAddTransactionDetailModelDto)
                                : t.toDto()
                        ),
                    }
                )
            ).data;
        } else if (!Array.isArray(transfer)) {
            templateName = transfer.templateName;
            templateId = transfer.templateId;
            transactions = transfer.transactions;
            response = (
                await this.internalTransfersClient.internalTransfersPost(
                    { type: 'add' },
                    dto as AddTransactionRequest
                )
            ).data;
        }

        // The API nulls out the templateName and templateId in the response but we'd like to retain them
        const correctedResponse = { ...response, templateName, templateId };

        // NOTE: hack to get the correct account display labels to show since the service POST response sends the wrong displayLabel
        if (correctedResponse.transactions) {
            correctedResponse.transactions = correctedResponse.transactions.map((t, i) => ({
                ...t,
                fromAccount: transactions ? transactions[i].fromAccount?.toDto() : t.fromAccount,
                toAccount: transactions ? transactions[i].toAccount?.toDto() : t.toAccount,
            }));
        }
        return new Transfer(correctedResponse);
    }

    // update transfer
    public async updateTransfer(transfer: {
        id: number;
        transactions: InternalTransferTransactionModelDto[];
    }) {
        const response = (
            await this.internalTransfersClient.internalTransfersPut(
                transfer.id,
                transfer.transactions[0]
            )
        ).data;

        // convert response as InternalTransferTransactionModelDto to AddTransactionRequest
        const correctedResponse: AddTransactionRequest = {
            id: response.id,
            // convert internalTransferTransactionModelDto to InternalTransferAddTransactionDetailModelDto
            transactions: [
                {
                    ...response,
                    fromAccount: response.fromAccount,
                    toAccount: response.toAccount,
                    amount: response.amount,
                    memo: response.memo,
                    paymentOptionType:
                        response.paymentOptionType as unknown as LoanPaymentOptionTypeEnumDto,
                    frequency: response.frequencyModel,
                },
            ],
        };

        return new Transfer(correctedResponse);
    }

    public async getTransferActivity(filter: TransferFilter, isRecurring = false) {
        const isRecurringFilter = (f: TransferFilter): f is RecurringFilter => isRecurring;
        const transfers = await (isRecurringFilter(filter)
            ? this.internalTransfersClient.internalTransfersGetRecurringTransfers(filter)
            : this.internalTransfersClient.internalTransfersFilterTransfers(filter));
        return transfers.data.map(transfer => new TransferPayment(transfer));
    }

    public async getTransferAccounts(type: string) {
        const accounts = await getTransferAccounts(type);
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferTemplateAccounts(type: string) {
        const accounts = await getTransferTemplateAccounts(type);
        return accounts.map(account => new TransferAccount(account));
    }

    public async getFilteredTransferAccounts(
        oppositeAccountId: number,
        type: string,
        isTemplate = false
    ) {
        const accounts = await getFilteredTransferAccounts(oppositeAccountId, type, isTemplate);
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferFromAccounts() {
        const accounts = await getTransferFromAccounts();
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferFilterFromAccounts() {
        const accounts = await getTransferFilterFromAccounts();
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferToAccounts(oppositeAccountId: number) {
        const accounts = await getTransferToAccounts(oppositeAccountId);
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferFilterToAccounts() {
        const accounts = await getTransferFilterToAccounts();
        return accounts.map(account => new TransferAccount(account));
    }

    public async getTransferById(id: string) {
        const response = (await this.http.request(`internalTransfers/?id=${id}`, {
            method: 'GET',
        })) as InternalTransferTransactionModelDto;
        const correctedResponse: AddTransactionRequest = {
            id: response.id,
            transactions: [
                {
                    ...response,
                    fromAccount: response.fromAccount,
                    toAccount: response.toAccount,
                    amount: response.amount,
                    memo: response.memo,
                    paymentOptionType:
                        response.paymentOptionType as unknown as LoanPaymentOptionTypeEnumDto,
                    frequency: response.frequencyModel,
                },
            ],
        };
        return new Transfer(correctedResponse);
    }

    public async getTransferTemplateById(id: string) {
        const response = await this.internalTransfersClient.internalTransfersGetTransferTemplate(
            Number(id)
        );
        return response.data;
    }

    // NOTE: The transfer template API endpoint urls are seemingly misnamed.  The 'internalTransfersGetTransferTemplate' request return the details DTO and the 'internalTransfersGetTransferTemplateDetails' returns the base template
    public async getTransferTemplateDetails(id: string) {
        const response = await this.internalTransfersClient.internalTransfersGetTransferTemplate(
            Number(id)
        );
        return new TransferTemplateDetails(response.data);
    }

    public async getTransferTemplate(id: string) {
        const response =
            await this.internalTransfersClient.internalTransfersGetTransferTemplateDetails(
                Number(id),
                {
                    // eslint-disable-next-line no-restricted-syntax
                    type: null,
                }
            );
        return new Transfer(response.data);
    }

    public async getTransferTemplates(body: object) {
        return this.http.request(`internalTransfers/getTransferTemplates`, {
            method: 'POST',
            body,
        });
    }

    public async deleteTransferTemplate(id: number) {
        return this.internalTransfersClient.internalTransfersDeleteTemplate(id);
    }

    public async createTransferTemplate(transfer: Transfer) {
        const dto = transfer.toDto();
        const response = await this.internalTransfersClient.internalTransfersSaveTransferTemplate(
            { type: transfer.type },
            dto
        );
        return new Transfer(response.data);
    }

    public async updateTransferTemplate(transfer: Transfer) {
        const response = await this.internalTransfersClient.internalTransfersUpdateTransferTemplate(
            transfer.toDto()
        );
        // API does not return the from/to accounts in the response so we need to map them back over from the original transfer
        response.data.transactions?.map((t, i) => {
            t.fromAccount = transfer.transactions[i].fromAccount?.toDto();
            t.toAccount = transfer.transactions[i].toAccount?.toDto();
            return t;
        });
        return new Transfer(response.data);
    }

    public async validateTemplateName(name: string) {
        const response = await this.internalTransfersClient.internalTransfersValidateTemplateName({
            templateName: name,
        });
        return response.data;
    }

    public async approveOrRejectTransferTemplate(type: 'Approved' | 'Rejected', id: number) {
        let action;
        if (type === 'Approved') {
            action = 'Approve';
        } else {
            action = 'Reject';
        }
        const response =
            await this.internalTransfersClient.internalTransfersApproveRejectTemplateGet(id, {
                type: action,
            });
        return response.data;
    }

    public async approveOrRejectTransferPayments(
        type: 'approve' | 'reject',
        transfers: TransferPayment[],
        comments: string
    ) {
        let action;
        if (type === 'approve') {
            action = 'Approve';
        } else {
            action = 'Reject';
        }
        const response =
            action === 'Approve'
                ? await this.internalTransfersClient.internalTransfersBatchApprove({
                      internalTransfers: transfers.map(t => ({ ...t.toDto() })),
                      comments,
                      id: 0,
                      updatedDate: new Date().toISOString(),
                      securityMessage: {
                          actionType: 'ApproveTransfer_ByName',
                          status: undefined,
                          methodUsed: undefined,
                          hasAlternate: false,
                          errorCode: undefined,
                          message: undefined,
                          oneTimePassword: undefined,
                          challengeMethodTypeId: 0,
                      },
                  })
                : await this.internalTransfersClient.internalTransfersBatchReject({
                      internalTransfers: transfers.map(t => ({ ...t.toDto() })),
                      comments,
                      id: 0,
                      updatedDate: new Date().toISOString(),
                      securityMessage: {
                          actionType: 'RejectTransfer_ByName',
                          status: undefined,
                          methodUsed: undefined,
                          hasAlternate: false,
                          errorCode: undefined,
                          message: undefined,
                          oneTimePassword: undefined,
                          challengeMethodTypeId: 0,
                      },
                  });
        return response.data;
    }

    public async cancelTransferPayment(transfer: TransferPayment, comments: string) {
        const transferDto = { ...transfer.toDto(), comments, internalTransferId: transfer.id };
        const response = await this.internalTransfersClient.internalTransfersCancel(transferDto);
        return response.data;
    }
}
